import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, X, User, IdCard, Upload, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import AuthNavbar from "../../components/navigations/AuthNavbar";
import clientService from "../../api/ClientService";
import SearchableSelect from "../../components/agents/dashboard/SearchableSelect";
import { ghanaRegions, ghanaLocationsByRegion } from "../../data/ghanaRegionsAndLocations";

export default function EditClient() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const client = location.state?.client;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idValidationErrors, setIdValidationErrors] = useState({});
  
  // Track original image URLs to detect changes
  const [originalImages, setOriginalImages] = useState({
    idCardFront: null,
    idCardBack: null,
  });

  const reverseDocumentTypeMapping = {
    "Ghana Card": "ghana_card",
    "Passport": "passport",
    "Driver License": "drivers_license",
    "Voter ID": "voter_id",
    "NHIS Card": "nhis_card",
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneDisplay: "",
    gender: "",
    dob: "",
    region: "",
    location: "",
    customLocation: "",
    // ID Information
    idType: "",
    issueDate: "",
    expiryDate: "",
    idFrontImage: null,
    idBackImage: null,
    card_number: "",
    ghanaCardMiddle: "",
    ghanaCardCheck: "",
  });

  // Initialize form with client data
  useEffect(() => {
    if (!client) {
      toast.error("No client data provided");
      navigate("/others-clients");
      return;
    }
    
    // Verify slug matches if provided in URL
    if (slug && client.slug !== slug) {
      toast.error("Client slug mismatch");
      navigate("/others-clients");
      return;
    }

    // Parse DOB from DDMMYYYY format
    const parseDOB = (dobString) => {
      if (!dobString || dobString.length !== 8) return "";
      const day = dobString.slice(0, 2);
      const month = dobString.slice(2, 4);
      const year = dobString.slice(4, 8);
      return `${year}-${month}-${day}`;
    };

    // Parse date to YYYY-MM-DD format for HTML date input
    const parseDateForInput = (dateString) => {
      if (!dateString) return "";
      try {
        // If it's already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        // Try to parse as Date object
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        // Format as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } catch {
        return "";
      }
    };

    // Parse Ghana Card number
    const parseGhanaCard = (cardNumber) => {
      if (!cardNumber || !cardNumber.startsWith("GHA")) return { middle: "", check: "" };
      const middle = cardNumber.slice(3, 12); // 9 digits
      const check = cardNumber.slice(12); // 1 digit
      return { middle, check };
    };

    // Format phone for display
    const formatPhoneForDisplay = (phone) => {
      if (!phone) return "";
      if (phone.startsWith("233")) {
        return `0${phone.slice(3)}`;
      }
      return phone;
    };

    const ghanaCardParts = parseGhanaCard(client.cardNumber || "");

    // Check if location is custom (not in standard locations list)
    const clientRegion = client.region || "";
    const clientLocation = client.location || "";
    let locationValue = clientLocation;
    let customLocationValue = "";
    
    if (clientRegion && clientLocation) {
      const standardLocations = ghanaLocationsByRegion[clientRegion] || [];
      const isCustomLocation = !standardLocations.includes(clientLocation);
      
      if (isCustomLocation) {
        locationValue = "Other";
        customLocationValue = clientLocation;
      }
    }

    setFormData({
      firstName: client.firstName || "",
      lastName: client.lastName || "",
      email: client.email || "",
      phone: client.phone || "",
      phoneDisplay: formatPhoneForDisplay(client.phone || ""),
      gender: client.gender ? client.gender.charAt(0).toUpperCase() + client.gender.slice(1).toLowerCase() : "",
      dob: parseDOB(client.dob || ""),
      region: clientRegion,
      location: locationValue,
      customLocation: customLocationValue,
      idType: client.idType || "",
      issueDate: parseDateForInput(client.issueDate || ""),
      expiryDate: parseDateForInput(client.expiryDate || ""),
      idFrontImage: client.idCardFront || null,
      idBackImage: client.idCardBack || null,
      card_number: client.cardNumber || "",
      ghanaCardMiddle: ghanaCardParts.middle,
      ghanaCardCheck: ghanaCardParts.check,
    });

    // Store original image URLs
    setOriginalImages({
      idCardFront: client.idCardFront || null,
      idCardBack: client.idCardBack || null,
    });
  }, [client, navigate, slug]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (idValidationErrors[field]) {
      setIdValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (value) => {
    // Remove any non-digit characters
    const digitsOnly = value.replace(/\D/g, "");
    
    // Format for display (0XXXXXXXXX)
    let formatted = "";
    if (digitsOnly.startsWith("233")) {
      formatted = `0${digitsOnly.slice(3)}`;
    } else if (digitsOnly.startsWith("0")) {
      formatted = digitsOnly;
    } else {
      formatted = digitsOnly;
    }
    
    // Limit to 10 digits for display
    if (formatted.length > 10) {
      formatted = formatted.slice(0, 10);
    }
    
    setFormData((prev) => ({
      ...prev,
      phoneDisplay: formatted,
      phone: formatted.startsWith("0") ? `233${formatted.slice(1)}` : formatted,
    }));
  };

  const isPhoneValid = () => {
    if (!formData.phoneDisplay) return false;
    if (formData.phoneDisplay.startsWith("233")) {
      return formData.phoneDisplay.length === 12;
    } else if (formData.phoneDisplay.startsWith("0")) {
      return formData.phoneDisplay.length === 10;
    }
    return false;
  };

  const handleImageUpload = (field, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (field) => {
    setFormData((prev) => ({ ...prev, [field]: null }));
    
    if (idValidationErrors[field]) {
      setIdValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Check if image has changed (is base64 now vs original URL)
  const isImageChanged = (currentImage, originalImage) => {
    if (!currentImage) return false;
    if (!originalImage) return currentImage.startsWith("data:image");
    // If current is base64 and different from original URL, it's changed
    return currentImage.startsWith("data:image") && currentImage !== originalImage;
  };

  // Format DOB as DDMMYYYY
  const formatDOB = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  const hasAnyIdField = () => {
    return !!(
      formData.idType ||
      formData.ghanaCardMiddle ||
      formData.ghanaCardCheck ||
      formData.issueDate ||
      formData.expiryDate ||
      formData.idFrontImage ||
      formData.idBackImage
    );
  };

  const validateIdFields = () => {
    const errors = {};
    
    if (hasAnyIdField()) {
      if (!formData.idType) {
        errors.idType = "ID Type is required when providing ID information";
      }
      if (!formData.ghanaCardMiddle?.trim()) {
        errors.ghanaCardMiddle = "Card number (middle 9 digits) is required";
      } else if (formData.ghanaCardMiddle.length !== 9) {
        errors.ghanaCardMiddle = "Card number must be exactly 9 digits";
      }
      if (!formData.ghanaCardCheck?.trim()) {
        errors.ghanaCardCheck = "Check digit is required";
      } else if (formData.ghanaCardCheck.length !== 1) {
        errors.ghanaCardCheck = "Check digit must be exactly 1 digit";
      }
      if (!formData.issueDate) {
        errors.issueDate = "Issue date is required";
      }
      if (!formData.expiryDate) {
        errors.expiryDate = "Expiry date is required";
      }
      if (!formData.idFrontImage) {
        errors.idFrontImage = "Front image is required";
      }
      if (!formData.idBackImage) {
        errors.idBackImage = "Back image is required";
      }
    }
    
    setIdValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    const requiredFields = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "gender",
      "dob",
      "region",
      "location",
    ];
    
    const allFieldsFilled = requiredFields.every((field) => {
      if (field === "location") {
        return formData.location || (formData.location === "Other" && formData.customLocation);
      }
      return formData[field]?.trim();
    });
    
    const phoneValid = isPhoneValid();
    const idFieldsValid = hasAnyIdField() ? validateIdFields() : true;
    
    return allFieldsFilled && phoneValid && idFieldsValid;
  };

  const createUpdatePayload = () => {
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone,
      location: formData.location === "Other" ? formData.customLocation : formData.location,
      dob: formatDOB(formData.dob),
      region: formData.region,
      gender: formData.gender.toLowerCase(),
    };

    // Handle ID information
    if (formData.idType) {
      payload.document_type = reverseDocumentTypeMapping[formData.idType] || "";
      
      // Combine Ghana Card parts
      if (formData.ghanaCardMiddle && formData.ghanaCardCheck) {
        payload.card_number = `GHA${formData.ghanaCardMiddle}${formData.ghanaCardCheck}`;
      }
      
      if (formData.issueDate) {
        payload.issue_date = formData.issueDate;
      }
      
      if (formData.expiryDate) {
        payload.expiry_date = formData.expiryDate;
      }
      
      // Handle images: if changed (base64), send base64; if unchanged (URL), send URL
      if (formData.idFrontImage) {
        if (isImageChanged(formData.idFrontImage, originalImages.idCardFront)) {
          payload.id_card_front = formData.idFrontImage; // base64
        } else {
          payload.id_card_front = originalImages.idCardFront; // original URL
        }
      }
      
      if (formData.idBackImage) {
        if (isImageChanged(formData.idBackImage, originalImages.idCardBack)) {
          payload.id_card_back = formData.idBackImage; // base64
        } else {
          payload.id_card_back = originalImages.idCardBack; // original URL
        }
      }
    }

    return payload;
  };

  const handleSubmit = async () => {
    if (hasAnyIdField()) {
      const idFieldsValid = validateIdFields();
      if (!idFieldsValid) {
        toast.error("Please complete all ID information fields or leave them all empty");
        return;
      }
    }

    if (!isFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createUpdatePayload();
      const response = await clientService.updateClient(client.slug, payload);

      if (response.status) {
        toast.success(response.message || "Client updated successfully!");
        navigate("/others-clients");
      } else {
        toast.error(response.message || "Failed to update client");
        if (response.validationErrors) {
          console.error("Validation errors:", response.validationErrors);
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!client) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      <AuthNavbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 shadow-lg">
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/others-clients")}
                className="p-2 text-white/80 transition-all duration-200 hover:text-white hover:bg-white/20 rounded-lg"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                  <User className="w-7 h-7" />
                  Edit Client
                </h1>
                <p className="mt-1 text-sm text-emerald-100">
                  Update client information
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 p-8"
        >
          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <User className="w-5 h-5 mr-2 text-emerald-600" />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phoneDisplay}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="0XXXXXXXXX"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                  Gender <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3">
                  {["Male", "Female"].map((option) => (
                    <motion.label
                      key={option}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center gap-3 px-4 py-3 flex-1 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        formData.gender.toLowerCase() === option.toLowerCase()
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      onClick={() => handleInputChange("gender", option)}
                    >
                      <div
                        className={`relative w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                          formData.gender.toLowerCase() === option.toLowerCase()
                            ? "border-emerald-500 bg-emerald-500"
                            : "border-gray-300"
                        }`}
                      >
                        {formData.gender.toLowerCase() === option.toLowerCase() && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute inset-0.5 rounded-full bg-white"
                          />
                        )}
                      </div>
                      <span
                        className={`text-sm font-medium transition-colors ${
                          formData.gender.toLowerCase() === option.toLowerCase()
                            ? "text-emerald-700"
                            : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </motion.label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleInputChange("dob", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Region *
                </label>
                <SearchableSelect
                  options={ghanaRegions}
                  value={formData.region}
                  onChange={(value) => {
                    handleInputChange("region", value);
                    handleInputChange("location", "");
                    handleInputChange("customLocation", "");
                  }}
                  placeholder="Select Region"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Location *
                </label>
                {formData.region ? (
                  <>
                    <SearchableSelect
                      options={ghanaLocationsByRegion[formData.region] || []}
                      value={formData.location}
                      onChange={(value) => {
                        if (value === "Other") {
                          handleInputChange("location", "Other");
                        } else {
                          handleInputChange("location", value);
                          handleInputChange("customLocation", "");
                        }
                      }}
                      placeholder="Select Location"
                      showOther={true}
                      otherPlaceholder="Enter custom location"
                      label="location"
                    />
                    {formData.location === "Other" && (
                      <input
                        type="text"
                        value={formData.customLocation}
                        onChange={(e) => handleInputChange("customLocation", e.target.value)}
                        className="mt-2 px-5 py-4 w-full text-base rounded-xl border-2 border-gray-200 bg-gray-50 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300"
                        placeholder="Enter your custom location"
                      />
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    placeholder="Enter Location"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                )}
              </div>
            </div>
          </div>

          {/* ID Information */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <IdCard className="w-5 h-5 mr-2 text-emerald-600" />
              ID Information (Optional)
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  ID Type
                </label>
                <select
                  value={formData.idType}
                  onChange={(e) => handleInputChange("idType", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Select ID Type</option>
                  <option value="Ghana Card">Ghana Card</option>
                  <option value="Passport">Passport</option>
                  <option value="Driver License">Driver License</option>
                  <option value="Voter ID">Voter ID</option>
                  <option value="NHIS Card">NHIS Card</option>
                </select>
                {idValidationErrors.idType && (
                  <p className="mt-1 text-sm text-red-600">{idValidationErrors.idType}</p>
                )}
              </div>
              {formData.idType === "Ghana Card" && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-base font-semibold text-gray-700">
                    Card Number {hasAnyIdField() && <span className="text-red-500">*</span>}
                  </label>
                  <div className="flex items-center gap-2 w-full">
                    {/* GHA prefix (readonly) */}
                    <div className="flex-shrink-0">
                      <input
                        type="text"
                        value="GHA"
                        readOnly
                        disabled={!formData.idType}
                        className="px-4 py-4 w-20 text-base rounded-xl border-2 border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed font-semibold uppercase text-center"
                      />
                    </div>
                    {/* First hyphen separator */}
                    <div className="flex-shrink-0 flex items-center justify-center w-6">
                      <span className="text-2xl font-semibold text-gray-500">-</span>
                    </div>
                    {/* Middle 9 digits */}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={formData.ghanaCardMiddle}
                        onChange={(e) => {
                          // Only allow digits, max 9
                          const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 9);
                          handleInputChange("ghanaCardMiddle", digits);
                        }}
                        disabled={!formData.idType}
                        placeholder="123456789"
                        maxLength={9}
                        className={`px-5 py-4 w-full text-base rounded-xl border-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                          idValidationErrors.ghanaCardMiddle
                            ? "border-red-500 bg-red-50"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      />
                    </div>
                    {/* Second hyphen separator */}
                    <div className="flex-shrink-0 flex items-center justify-center w-6">
                      <span className="text-2xl font-semibold text-gray-500">-</span>
                    </div>
                    {/* Check digit */}
                    <div className="flex-shrink-0">
                      <input
                        type="text"
                        value={formData.ghanaCardCheck}
                        onChange={(e) => {
                          // Only allow digits, max 1
                          const digit = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);
                          handleInputChange("ghanaCardCheck", digit);
                        }}
                        disabled={!formData.idType}
                        placeholder="X"
                        maxLength={1}
                        className={`px-4 py-4 w-16 text-base rounded-xl border-2 transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:bg-white hover:border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed text-center ${
                          idValidationErrors.ghanaCardCheck
                            ? "border-red-500 bg-red-50"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      />
                    </div>
                  </div>
                  {idValidationErrors.ghanaCardMiddle && (
                    <p className="text-sm text-red-600 mt-1">{idValidationErrors.ghanaCardMiddle}</p>
                  )}
                  {idValidationErrors.ghanaCardCheck && (
                    <p className="text-sm text-red-600 mt-1">{idValidationErrors.ghanaCardCheck}</p>
                  )}
                </div>
              )}
              {formData.idType && formData.idType !== "Ghana Card" && (
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Card Number *
                  </label>
                  <input
                    type="text"
                    value={formData.card_number}
                    onChange={(e) => handleInputChange("card_number", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              )}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Issue Date
                </label>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={(e) => handleInputChange("issueDate", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                {idValidationErrors.issueDate && (
                  <p className="mt-1 text-sm text-red-600">{idValidationErrors.issueDate}</p>
                )}
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => handleInputChange("expiryDate", e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                {idValidationErrors.expiryDate && (
                  <p className="mt-1 text-sm text-red-600">{idValidationErrors.expiryDate}</p>
                )}
              </div>
            </div>

            {/* ID Images */}
            <div className="mt-6">
              <label className="block mb-4 text-sm font-medium text-gray-700">
                ID Card Images
              </label>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Front Image */}
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Front Image
                  </label>
                  {formData.idFrontImage ? (
                    <div className="relative">
                      <img
                        src={formData.idFrontImage}
                        alt="ID Front"
                        className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                      />
                      <button
                        onClick={() => removeImage("idFrontImage")}
                        className="absolute -top-2 -right-2 flex items-center justify-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg cursor-pointer hover:bg-emerald-700 transition-colors">
                        <Upload className="inline w-3 h-3 mr-1" />
                        Change
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleImageUpload("idFrontImage", e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-emerald-400 transition-colors">
                      <div className="flex flex-col items-center justify-center h-full text-gray-600">
                        <Upload className="mb-2 w-8 h-8" />
                        <span className="text-sm">Upload Front Image</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && handleImageUpload("idFrontImage", e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  )}
                  {idValidationErrors.idFrontImage && (
                    <p className="mt-1 text-sm text-red-600">{idValidationErrors.idFrontImage}</p>
                  )}
                </div>

                {/* Back Image */}
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Back Image
                  </label>
                  {formData.idBackImage ? (
                    <div className="relative">
                      <img
                        src={formData.idBackImage}
                        alt="ID Back"
                        className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                      />
                      <button
                        onClick={() => removeImage("idBackImage")}
                        className="absolute -top-2 -right-2 flex items-center justify-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg cursor-pointer hover:bg-emerald-700 transition-colors">
                        <Upload className="inline w-3 h-3 mr-1" />
                        Change
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleImageUpload("idBackImage", e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-emerald-400 transition-colors">
                      <div className="flex flex-col items-center justify-center h-full text-gray-600">
                        <Upload className="mb-2 w-8 h-8" />
                        <span className="text-sm">Upload Back Image</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && handleImageUpload("idBackImage", e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  )}
                  {idValidationErrors.idBackImage && (
                    <p className="mt-1 text-sm text-red-600">{idValidationErrors.idBackImage}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/others-clients")}
              className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Client"
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
